import {
  RANDOM_SWAP_PROMPTS,
  SWAP_PROMPTS,
  type SwapPrompt,
} from "@somethings-ui/core/swap-prompts";
import { DateTime } from "luxon";
import type { HubView } from "@/components/hub/use-hub-store";
import {
  hubViewForActivityType,
  hubViewForContentEntityType,
} from "@/components/hub/hub-view-for-entity";
import type {
  EngagementDayCounts,
  EngagementSummary,
} from "@/lib/api/mentorships";
import type {
  ShareableEntity,
  ShareableEntitySwap,
} from "@/lib/api/shareable-entities";
import {
  dayKeyOf,
  formatDateSeparatorLabel,
} from "../../attention/attention-view";
import { getActivityTitle, isActivityType } from "@/lib/activities/registry";
import type { ActionToolGroup } from "../action-analytics";
import type { ActivityToolId, ContentToolId } from "../tools/tool-config";

export const HISTORY_DAY_TIMEZONE_FALLBACK = "America/New_York";

export interface HistoryEntry {
  id: string;
  group: ActionToolGroup;
  activityToolId: ActivityToolId | null;
  contentToolId: ContentToolId | null;
  title: string;
  subline: string;
  dateISO: string;
  /** Mentee-authored and not yet read by the current user; drives the unread dot. */
  isUnread: boolean;
  /** Moment image URL, or swap mentor image (front); signed at render. */
  thumbnailUrl: string | null;
  /** Swap mentee image (back); null → transparent placeholder. */
  secondaryThumbnailUrl: string | null;
  /** Note body for the paper thumbnail preview. */
  noteContent: string | null;
}

export interface HistoryDayGroup {
  dayKey: string;
  label: string;
  entries: HistoryEntry[];
}

/** Per-day marker categories, one dot each in the calendar cell. */
export interface DayCategories {
  activity: boolean;
  content: boolean;
  messages: boolean;
  videoCall: boolean;
}

export interface MonthSummary {
  activitiesCount: number;
  contentCount: number;
  messagesCount: number;
  videoCallCount: number;
  daysEngaged: number;
  mostUsedActivityToolId: ActivityToolId | null;
  hasEngagement: boolean;
}

export interface DayRundown {
  activitiesCount: number;
  contentCount: number;
  messagesCount: number;
  videoCallCount: number;
  hasEngagement: boolean;
}

const EMPTY_DAY_COUNTS: EngagementDayCounts = {
  activities: 0,
  content: 0,
  messages: 0,
  videoCalls: 0,
};

export function activityToolTitle(id: ActivityToolId): string {
  return getActivityTitle(id);
}

/** Maps a History list entry to the Hub viewer view for that artifact. */
export function hubViewForHistoryEntry(entry: HistoryEntry): HubView | null {
  if (entry.activityToolId) {
    return hubViewForActivityType(entry.activityToolId);
  }
  if (entry.contentToolId) {
    return hubViewForContentEntityType(entry.contentToolId);
  }
  return null;
}

function asActivityToolId(type: string): ActivityToolId | null {
  return isActivityType(type) ? type : null;
}

function activitySubline(status: string): string {
  switch (status) {
    case "completed":
      return "Completed";
    case "in_progress":
      return "In progress";
    case "declined":
      return "Declined";
    default:
      return "Sent";
  }
}

function swapPromptTitle(swap: ShareableEntitySwap): string {
  if (swap.promptId === "custom") return swap.customTitle ?? "Swap";
  const prompt: SwapPrompt | undefined =
    SWAP_PROMPTS.find((p) => p.id === swap.promptId) ??
    RANDOM_SWAP_PROMPTS.find((p) => p.id === swap.promptId);
  return prompt?.title ?? "Swap";
}

function notePreview(content: string): string {
  const flattened = content.replace(/\s+/g, " ").trim();
  return flattened.length > 80 ? `${flattened.slice(0, 80)}…` : flattened;
}

/**
 * Shareable-entity timestamps sometimes arrive as Postgres SQL
 * (`2026-07-16 14:53:35.445+00`) which Luxon `fromISO` rejects. Normalize to
 * ISO so day bucketing / relative time stay valid.
 */
export function parseEntityTimestamp(value: string): DateTime {
  const fromIso = DateTime.fromISO(value);
  if (fromIso.isValid) return fromIso;

  // `+00` → `+00:00`; space → `T` so fromISO can retry before fromSQL.
  const normalized = value.replace(" ", "T").replace(/([+-]\d{2})$/, "$1:00");
  const retried = DateTime.fromISO(normalized);
  if (retried.isValid) return retried;

  const fromSql = DateTime.fromSQL(value);
  return fromSql.isValid ? fromSql : DateTime.invalid("unparseable timestamp");
}

function toNormalizedISO(value: string): string {
  if (DateTime.fromISO(value).isValid) return value;
  const parsed = parseEntityTimestamp(value);
  return parsed.isValid ? parsed.toUTC().toISO() ?? value : value;
}

/** Maps a shareable entity to a history entry, or `null` when it's not history-worthy. */
export function toHistoryEntry(entity: ShareableEntity): HistoryEntry | null {
  const fields = toHistoryEntryFields(entity);
  if (!fields) return null;
  return { ...fields, isUnread: entity.isNew ?? false };
}

function toHistoryEntryFields(
  entity: ShareableEntity,
): Omit<HistoryEntry, "isUnread"> | null {
  if (entity.status === "draft" || entity.status === "scheduled") return null;

  switch (entity.entityType) {
    case "activity": {
      const activity = entity.activity;
      if (!activity) return null;
      const toolId = asActivityToolId(activity.activityType);
      if (!toolId) return null;
      return {
        id: entity.id,
        group: "activity",
        activityToolId: toolId,
        contentToolId: null,
        title: getActivityTitle(toolId),
        subline: activitySubline(activity.status),
        dateISO: toNormalizedISO(activity.completedAt ?? entity.created_at),
        thumbnailUrl: null,
        secondaryThumbnailUrl: null,
        noteContent: null,
      };
    }
    case "swap": {
      if (!entity.swap) return null;
      return {
        id: entity.id,
        group: "content",
        activityToolId: null,
        contentToolId: "swap",
        title: "Swap",
        subline: swapPromptTitle(entity.swap),
        dateISO: toNormalizedISO(entity.created_at),
        // Same stacking as Hub SwapCard: mentor front, mentee back.
        thumbnailUrl: entity.swap.mentorImage?.thumbnailUrl ?? null,
        secondaryThumbnailUrl: entity.swap.menteeImage?.thumbnailUrl ?? null,
        noteContent: null,
      };
    }
    case "moment": {
      if (!entity.moment) return null;
      return {
        id: entity.id,
        group: "content",
        activityToolId: null,
        contentToolId: "moment",
        title: "Moment",
        subline: entity.moment.caption?.trim() || "Photo",
        dateISO: toNormalizedISO(entity.created_at),
        thumbnailUrl: entity.moment.images[0]?.thumbnailUrl ?? null,
        secondaryThumbnailUrl: null,
        noteContent: null,
      };
    }
    case "note": {
      if (!entity.note) return null;
      return {
        id: entity.id,
        group: "content",
        activityToolId: null,
        contentToolId: "note",
        title: entity.title?.trim() || "Note",
        subline: notePreview(entity.note.content),
        dateISO: toNormalizedISO(entity.created_at),
        thumbnailUrl: null,
        secondaryThumbnailUrl: null,
        noteContent: entity.note.content,
      };
    }
    default: {
      // The API can return entity types the client doesn't surface in history
      // (e.g. "story"); skip them rather than emit a malformed entry.
      entity.entityType satisfies never;
      return null;
    }
  }
}

export function toHistoryEntries(entities: ShareableEntity[]): HistoryEntry[] {
  return entities
    .map(toHistoryEntry)
    .filter((entry): entry is HistoryEntry => entry !== null);
}

function dateMillis(iso: string): number {
  const parsed = parseEntityTimestamp(iso);
  return parsed.isValid ? parsed.toMillis() : 0;
}

function sortByDateDesc(a: HistoryEntry, b: HistoryEntry): number {
  return dateMillis(b.dateISO) - dateMillis(a.dateISO);
}

/** Newest-first entries grouped into newest-first day buckets. */
export function groupEntriesByDay(
  entries: HistoryEntry[],
  now: DateTime = DateTime.now(),
  zone: string = HISTORY_DAY_TIMEZONE_FALLBACK,
): HistoryDayGroup[] {
  const groups = new Map<string, HistoryEntry[]>();
  for (const entry of entries) {
    const dayKey = dayKeyOf(entry.dateISO, zone);
    if (dayKey === null) continue;
    const bucket = groups.get(dayKey);
    if (bucket) bucket.push(entry);
    else groups.set(dayKey, [entry]);
  }

  return [...groups.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([dayKey, dayEntries]) => {
      const sorted = [...dayEntries].sort(sortByDateDesc);
      return {
        dayKey,
        label: formatDateSeparatorLabel(sorted[0].dateISO, now),
        entries: sorted,
      };
    });
}

/** Maps the BE engagement-summary day map into per-day calendar marker flags. */
export function dayCategoriesFromSummary(
  days: EngagementSummary["days"],
): Record<string, DayCategories> {
  const map: Record<string, DayCategories> = {};
  for (const [dayKey, counts] of Object.entries(days)) {
    const categories: DayCategories = {
      activity: counts.activities > 0,
      content: counts.content > 0,
      messages: counts.messages > 0,
      videoCall: counts.videoCalls > 0,
    };
    if (
      categories.activity ||
      categories.content ||
      categories.messages ||
      categories.videoCall
    ) {
      map[dayKey] = categories;
    }
  }
  return map;
}

export function monthSummaryFromTotals(
  totals: EngagementSummary["totals"],
): MonthSummary {
  const mostUsedActivityToolId = totals.mostUsedActivityType
    ? asActivityToolId(totals.mostUsedActivityType)
    : null;

  return {
    activitiesCount: totals.activities,
    contentCount: totals.content,
    messagesCount: totals.messages,
    videoCallCount: totals.videoCalls,
    daysEngaged: totals.daysEngaged,
    mostUsedActivityToolId,
    hasEngagement:
      totals.activities > 0 ||
      totals.content > 0 ||
      totals.messages > 0 ||
      totals.videoCalls > 0,
  };
}

export function dayRundownFromCounts(
  counts: EngagementDayCounts | undefined,
): DayRundown {
  const day = counts ?? EMPTY_DAY_COUNTS;
  return {
    activitiesCount: day.activities,
    contentCount: day.content,
    messagesCount: day.messages,
    videoCallCount: day.videoCalls,
    hasEngagement:
      day.activities > 0 ||
      day.content > 0 ||
      day.messages > 0 ||
      day.videoCalls > 0,
  };
}

/** Compact relative time for row timestamps: `now`, `5m`, `3h`, `2d`, `4w`, `6mo`, `1y`. */
export function formatCompactRelativeTime(
  iso: string,
  now: DateTime = DateTime.now(),
): string {
  const parsed = parseEntityTimestamp(iso);
  if (!parsed.isValid) return "";
  const diff = now
    .diff(parsed, ["years", "months", "weeks", "days", "hours", "minutes"])
    .toObject();
  const years = Math.floor(diff.years ?? 0);
  if (years >= 1) return `${years}y`;
  const months = Math.floor(diff.months ?? 0);
  if (months >= 1) return `${months}mo`;
  const weeks = Math.floor(diff.weeks ?? 0);
  if (weeks >= 1) return `${weeks}w`;
  const days = Math.floor(diff.days ?? 0);
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(diff.hours ?? 0);
  if (hours >= 1) return `${hours}h`;
  const minutes = Math.floor(diff.minutes ?? 0);
  if (minutes >= 1) return `${minutes}m`;
  return "now";
}
